'use client';

import Image from 'next/image';
import { type JSX } from 'react';

import { cn, getAccessibleFocusStyles, Text } from '@videoblocks/design-system';

import { type UseCaseItem } from './UseCaseSelector';

export type UseCaseCardProps = {
    useCase: UseCaseItem;
    isActive: boolean;
    disabled: boolean;
    onSelect: (id: string) => void;
};

export function UseCaseCard({ useCase, isActive, disabled, onSelect }: UseCaseCardProps): JSX.Element {
    return (
        <button
            type="button"
            disabled={disabled}
            aria-label={`Select ${useCase.title}`}
            title={useCase.title}
            aria-pressed={isActive}
            onClick={() => onSelect(useCase.id)}
            className={cn(
                'relative aspect-116/72 overflow-hidden rounded-md cursor-pointer border-2 border-transparent',
                isActive && 'border-yellow-400',
                disabled && 'opacity-50 cursor-not-allowed',
                getAccessibleFocusStyles(),
            )}
        >
            <Image
                src={useCase.imgUrl}
                alt={useCase.title}
                fill
                sizes="(min-width: 1200px) 220px, (min-width: 672px) 116px, 50vw"
                className="object-cover"
                draggable={false}
                priority
            />
            <Text asChild fontSize="xs">
                <span
                    className={cn(
                        'absolute bottom-1.5 left-1.5 truncate max-w-[calc(100%-12px)]',
                        'dark:text-neutral-dark-1000',
                        '@min-[320px]:text-sm @min-[320px]:bottom-2 @min-[320px]:left-2',
                        '[text-shadow:0px_0px_4.64px_rgba(0,0,0,0.6)]',
                    )}
                >
                    {useCase.title}
                </span>
            </Text>
        </button>
    );
}
